/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Toast,
  Tabs,
  Tab,
  Modal,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { UserOrganisation } from '../types/types';
import HumanRightsQuestionnaire from '../../src/assets/HumanRightsQuestionnaire.png';
import EnvironmentalProtection from '../../src/assets/EnvironmentalProtection.png';
import ESGDueDilligence from '../../src/assets/ESGDueDiligence.png';
import ESGDueDiligence1 from '../../src/assets/ESGDueDiligence1.png';
import CSDDDQuestionnaire from '../../src/assets/CSDDD-questionnaire.jpg';
import FDVQuestionnaire from '../../src/assets/FDVQuestionnaire.jpg';
import ESRS from '../../src/assets/ESRS.jpg';
import ESGBadge from '../../src/assets/ESGBadgRround.png';
import CSDDDBadge from '../../src/assets/CSDDDBadgeRound.png';
import CSRDBadge from '../../src/assets/CSRDBadgeRound.png';
import LKSGBadge from '../../src/assets/LkSGBadgeRound.png';
import FDVBadge from '../../src/assets/FDVBadgeRound.png';

import { checkSelfAssessmentTemplateEnabled } from '../util/util';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { captureSupabaseError } from '../lib/sentry';
import { tracking } from '../util/tracking';

const templateImages: Record<string, string> = {
  human_rights_labour: HumanRightsQuestionnaire,
  env_protection: EnvironmentalProtection,
  supplier_esg_report: ESGDueDilligence,
  supplier_lksg_report: ESGDueDiligence1,
  supplier_csddd_report: CSDDDQuestionnaire,
  supplier_fdv_report: FDVQuestionnaire,
  esrs_vsme_2026: ESRS,
  // Add more templates as needed
};

const templateBadges: Record<string, string> = {
  supplier_esg_report: ESGBadge,
  supplier_lksg_report: LKSGBadge,
  supplier_csddd_report: CSDDDBadge,
  supplier_fdv_report: FDVBadge,
  supplier_csrd_report: CSRDBadge,
};

export type CreateAssessmentResponse = {
  id: string;
  status: string;
  completed_at: string | null;
  is_existing: boolean;
};

type LatestAssessment = {
  completed_at?: string | null;
  created_at?: string | null;
};

const getAssessmentActionState = (latest?: LatestAssessment | null) => {
  let buttonLabel = 'Start';
  let buttonVariant: 'primary' | 'outline-primary' = 'primary';
  let notAvailable = false;
  let helperText: string | null = null;

  if (!latest) {
    return { buttonLabel, buttonVariant, notAvailable, helperText };
  }

  if (latest.completed_at) {
    const completedDate = new Date(latest.completed_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    if (completedDate > thirtyDaysAgo) {
      const date = completedDate.toLocaleDateString();
      const retakeDate = new Date(
        completedDate.getTime() + 30 * 24 * 60 * 60 * 1000,
      );

      buttonLabel = `Completed on ${date}`;
      notAvailable = true;
      helperText = `Completed. You can retake this assessment on ${retakeDate.toLocaleDateString()}`;
    } else {
      buttonLabel = 'Retake';
    }
  } else {
    buttonLabel = 'Continue';
    buttonVariant = 'outline-primary';

    if (latest.created_at) {
      helperText = `Started: ${new Date(latest.created_at).toLocaleDateString()}`;
    }
  }

  return { buttonLabel, buttonVariant, notAvailable, helperText };
};

const SelfAssessment = () => {
  const { selectedOrganisation, user } = useSelector((state: any) => ({
    selectedOrganisation: state.organisation.selected,
    user: state.auth.user,
  })) as {
    selectedOrganisation: UserOrganisation | undefined;
    user?: { id?: string };
  };

  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingAssessment, setCreatingAssessment] = useState(false);
  const [toastError, setToastError] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestText, setRequestText] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [extendedSearch, setExtendedSearch] = useState('');
  const [debouncedExtendedSearch, setDebouncedExtendedSearch] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedExtendedSearch(extendedSearch);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [extendedSearch]);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!selectedOrganisation?.organisation?.id) return;

      setLoading(true);
      setToastError(null);

      const { data, error } = await supabase.rpc(
        'get_self_assessment_templates',
        { org_id: selectedOrganisation.organisation.id },
      );

      setLoading(false);

      if (error) {
        console.error('Error fetching templates:', error);
        setToastError('Failed to load templates.');
      } else {
        setTemplates(data || []);
      }
    };

    fetchTemplates();
  }, [selectedOrganisation]);

  const startAssessment = async (templateId: string, templateKey: string) => {
    const assessmentEnabled = checkSelfAssessmentTemplateEnabled(
      selectedOrganisation?.organisation?.plan?.features ?? null,
      templateKey,
    );

    //TODO: if the assessment is not enabled, show a modal with upsell information
    if (!assessmentEnabled) {
      setShowUpgradeModal(true);
      return;
    }

    setCreatingAssessment(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        'get-or-create-assessment',
        {
          body: {
            org_id: selectedOrganisation?.organisation?.id,
            tmpl_id: templateId,
          },
        },
      );

      setCreatingAssessment(false);

      if (error) {
        console.error('Error:', error);
        setToastError('Failed to start assessment.');
        captureSupabaseError(error, { op: 'assessment.start' });
        return;
      }

      const assessment: CreateAssessmentResponse = data;

      if (
        assessment.status === 'completed' &&
        assessment.completed_at &&
        assessment.is_existing
      ) {
        // TODO show modal instead of toast
        setToastError(
          'You have completed this assessment within the last 30 days. Please come back later.',
        );
      } else if (assessment.id) {
        navigate(`/assessment/${assessment.id}`);
      }
    } catch (error) {
      captureSupabaseError(error, { op: 'assessment.start' });
      console.error('Error:', error);
      setToastError('Failed to start assessment. Please try again.');
      setCreatingAssessment(false);
    }
  };

  const openRequestModal = () => {
    setRequestText('');
    setRequestSubmitted(false);
    setShowRequestModal(true);
  };

  const closeRequestModal = () => {
    if (requestSubmitting) return;
    setShowRequestModal(false);
    setRequestText('');
    setRequestSubmitted(false);
  };

  const handleRequestQuestionnaire = async () => {
    if (!requestText.trim() || !user?.id) {
      return;
    }

    setRequestSubmitting(true);

    const { error } = await supabase.from('questionnaire_requests').insert({
      request_text: requestText.trim(),
      submitted_by: user.id,
      submitted_at: new Date().toISOString(),
    });

    if (error) {
      captureSupabaseError(error, { op: 'questionnaire.request' });
      setToastError('Failed to submit questionnaire request.');
      setRequestSubmitting(false);
      return;
    }

    setRequestSubmitted(true);
    setRequestSubmitting(false);
  };

  const essentialTemplates = templates.filter(
    (t) => t.key && templateImages[t.key],
  );

  const extendedTemplates = templates.filter(
    (t) => !t.key || !templateImages[t.key],
  );

  const filteredExtendedTemplates = extendedTemplates.filter((template) =>
    template.title
      ?.toLowerCase()
      .includes(debouncedExtendedSearch.trim().toLowerCase()),
  );

  return (
    <Container fluid>
      <h3>Your self assessment questionnaires</h3>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : null}

      <p>
        To get your certification, please complete the following assessments
      </p>

      <Tabs defaultActiveKey="essential" className="mb-4">
        <Tab eventKey="essential" title="Essential">
          <Row xs={1} sm={2} md={3} lg={3} className="g-4 mt-2">
            {essentialTemplates.map((template) => {
              const assessmentEnabled = checkSelfAssessmentTemplateEnabled(
                selectedOrganisation?.organisation?.plan?.features ?? null,
                template.key,
              );

              const imageSrc =
                template.key && templateImages[template.key]
                  ? templateImages[template.key]
                  : HumanRightsQuestionnaire;

              const badgeSrc =
                template.key && templateBadges[template.key]
                  ? templateBadges[template.key]
                  : ESGBadge;

              const latest = template.latest_assessment;
              const { buttonLabel, buttonVariant, notAvailable, helperText } =
                getAssessmentActionState(latest);

              return (
                <Col key={template.id}>
                  <Card className="h-100">
                    <Card.Img
                      variant="top"
                      src={imageSrc}
                      alt={template.title}
                    />
                    {badgeSrc && (
                      <img
                        src={badgeSrc}
                        alt={`${template.title} badge`}
                        style={{
                          width: '100px',
                          height: '100px',
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                        }}
                      />
                    )}
                    <Card.Body>
                      <Card.Title>{template.title}</Card.Title>
                      {/* <p>Questions: {template.question_count}</p> */}
                      <p>
                        This assessment awards the following badge(s) upon
                        completion:
                        {template.tags?.map((t: any) => (
                          <Badge key={t} bg="primary" className="mx-1">
                            {t.toUpperCase()}
                          </Badge>
                        ))}
                      </p>
                    </Card.Body>
                    <Card.Footer className="d-flex justify-content-space-between">
                      {!assessmentEnabled ? (
                        <span className="text-muted">
                          This assessment is not available on your plan. Please{' '}
                          <Link to="/settings?tab=organisation">upgrade</Link>{' '}
                          to access it.
                        </span>
                      ) : helperText ? (
                        <span className="text-muted">{helperText}</span>
                      ) : null}
                      {!notAvailable && assessmentEnabled && (
                        <Button
                          variant={buttonVariant}
                          size="sm"
                          className="ms-auto"
                          disabled={creatingAssessment}
                          onClick={() => {
                            tracking.log('UI', 'self_assessment_start', {
                              templateId: template.id,
                              action: buttonLabel,
                            });
                            startAssessment(template.id, template.key);
                          }}
                        >
                          {creatingAssessment ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            buttonLabel
                          )}
                        </Button>
                      )}
                    </Card.Footer>
                  </Card>
                </Col>
              );
            })}

            {essentialTemplates.length === 0 && (
              <Col>
                <p>No default templates available.</p>
              </Col>
            )}
          </Row>
        </Tab>
        <Tab eventKey="extended" title="Extended">
          <div className="mt-3">
            <Form.Group className="mb-3" controlId="extendedTemplatesSearch">
              <Form.Control
                type="search"
                placeholder="Search questionnaires by title"
                value={extendedSearch}
                onChange={(event) => setExtendedSearch(event.target.value)}
              />
            </Form.Group>

            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
              <p className="mb-0 text-muted">
                Don't see the questionnaire you're looking for? Please let us
                know and we will add it to the platform.
              </p>
              <Button variant="outline-primary" onClick={openRequestModal}>
                Request questionnaire
              </Button>
            </div>

            {filteredExtendedTemplates.map((template) => {
              const latest = template.latest_assessment;
              const { buttonLabel, notAvailable, helperText } =
                getAssessmentActionState(latest);

              return (
                <Card key={template.id} className="mb-3">
                  <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">{template.title}</h6>
                      {/* <small className="text-muted">
                        Questions: {template.question_count}
                      </small> */}
                      {helperText && (
                        <div className="small text-muted mt-1">
                          {helperText}
                        </div>
                      )}
                    </div>
                    {!notAvailable && (
                      <Button
                        size="sm"
                        disabled={creatingAssessment}
                        onClick={() => {
                          tracking.log('UI', 'self_assessment_start', {
                            templateId: template.id,
                            action: buttonLabel,
                          });
                          startAssessment(template.id, template.key);
                        }}
                      >
                        {buttonLabel}
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
            {filteredExtendedTemplates.length === 0 && (
              <p>No extended templates available.</p>
            )}
          </div>
        </Tab>
      </Tabs>

      {toastError && (
        <Toast
          onClose={() => setToastError(null)}
          show={!!toastError}
          delay={3000}
          autohide
        >
          <Toast.Body>{toastError}</Toast.Body>
        </Toast>
      )}
      <Modal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Upgrade Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This assessment is not available on your current plan{' '}
          <b>({selectedOrganisation?.organisation?.plan?.name})</b>. Please
          upgrade to access this feature.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowUpgradeModal(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowUpgradeModal(false);
              navigate('/settings?tab=organisation');
            }}
          >
            Upgrade Now
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showRequestModal} onHide={closeRequestModal} centered>
        <Modal.Header closeButton={!requestSubmitting}>
          <Modal.Title>Request questionnaire</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {requestSubmitted ? (
            <div className="text-center py-3">
              <h5 className="mb-2">Thank you</h5>
              <p className="mb-0">
                Your questionnaire request has been received.
              </p>
            </div>
          ) : (
            <Form.Group controlId="questionnaireRequestText">
              <Form.Label>Please describe the questionnire</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={requestText}
                onChange={(event) => setRequestText(event.target.value)}
                disabled={requestSubmitting}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          {requestSubmitted ? (
            <Button variant="primary" onClick={closeRequestModal}>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline-secondary"
                onClick={closeRequestModal}
                disabled={requestSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleRequestQuestionnaire}
                disabled={requestSubmitting || !requestText.trim()}
              >
                {requestSubmitting ? 'Requesting...' : 'Request'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SelfAssessment;
